"use client";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSuppliers } from "@/state/suppliers/supplier.slice";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Loader2, ArrowLeft, Save } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

export default function EditPurchasePage() {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { toast } = useToast();

    const supplierState = useSelector((state) => state.supplier || {});
    const suppliers = Array.isArray(supplierState.suppliers) ? supplierState.suppliers : supplierState.suppliers?.data || [];

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [purchase, setPurchase] = useState(null);
    const [formData, setFormData] = useState({
        supplier_id: "",
        invoice_number: "",
        purchase_status: "received",
        due_date: "",
        discount_type: "none",
        discount_value: 0,
        tax_rate: 0,
        notes: "",
    });

    useEffect(() => {
        dispatch(getSuppliers());
    }, [dispatch]);

    useEffect(() => {
        const fetchPurchase = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API_URL}purchases/${id}`, {
                    withCredentials: true,
                });
                const data = res.data?.data;
                setPurchase(data);
                setFormData({
                    supplier_id: data.supplier_id?.toString() || "",
                    invoice_number: data.invoice_number || "",
                    purchase_status: data.purchase_status || "received",
                    due_date: data.due_date ? new Date(data.due_date).toISOString().split("T")[0] : "",
                    discount_type: data.discount_type || "none",
                    discount_value: parseFloat(data.discount_value) || 0,
                    tax_rate: parseFloat(data.tax_rate) || 0,
                    notes: data.notes || "",
                });
            } catch (err) {
                console.error("Failed to fetch purchase:", err);
                toast({ variant: "destructive", title: "Error", description: "Failed to load purchase" });
            } finally {
                setLoading(false);
            }
        };
        if (id) fetchPurchase();
    }, [id, toast]);

    // Calculations
    const subtotal = parseFloat(purchase?.subtotal) || parseFloat(purchase?.total) || 0;
    const discountAmount = formData.discount_type === "percentage"
        ? (subtotal * formData.discount_value) / 100
        : formData.discount_type === "fixed"
            ? formData.discount_value
            : 0;
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = formData.tax_rate > 0 ? (taxableAmount * formData.tax_rate) / 100 : 0;
    const grandTotal = taxableAmount + taxAmount;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.put(`${API_URL}purchases/${id}`, {
                supplier_id: formData.supplier_id,
                invoice_number: formData.invoice_number,
                discount_type: formData.discount_type,
                discount_value: parseFloat(formData.discount_value) || 0,
                tax_rate: parseFloat(formData.tax_rate) || 0,
                purchase_status: formData.purchase_status,
                due_date: formData.due_date || null,
                notes: formData.notes,
            }, { withCredentials: true });

            toast({ title: "Success", description: "Purchase updated successfully" });
            navigate("/dashboard/purchases");
        } catch (err) {
            console.error("Failed to update purchase:", err);
            toast({ variant: "destructive", title: "Error", description: err.response?.data?.message || "Failed to update" });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="mx-auto p-4 space-y-6">
            <div className="flex items-center gap-4">
                <Link to="/dashboard/purchases">
                    <Button variant="ghost" size="icon">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                </Link>
                <h1 className="text-3xl font-bold">Edit Purchase</h1>
                <span className="text-muted-foreground">#{purchase?.invoice_number || id}</span>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                        {/* Purchase Details */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Purchase Details</CardTitle>
                            </CardHeader>
                            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div className="col-span-2">
                                    <Label>Supplier</Label>
                                    <Select
                                        value={formData.supplier_id}
                                        onValueChange={(val) => setFormData({ ...formData, supplier_id: val })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Supplier" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {suppliers?.map((s) => (
                                                <SelectItem key={s.id} value={s.id.toString()}>
                                                    {s.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Reference No.</Label>
                                    <Input
                                        value={formData.invoice_number}
                                        onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label>Status</Label>
                                    <Select
                                        value={formData.purchase_status}
                                        onValueChange={(val) => setFormData({ ...formData, purchase_status: val })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="ordered">Ordered</SelectItem>
                                            <SelectItem value="pending">Pending</SelectItem>
                                            <SelectItem value="received">Received</SelectItem>
                                            <SelectItem value="cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Due Date</Label>
                                    <Input
                                        type="date"
                                        value={formData.due_date}
                                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Items (read-only) */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Items (Read Only)</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Product</TableHead>
                                            <TableHead>Quantity</TableHead>
                                            <TableHead>Unit Cost</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(purchase?.Stocks || []).map((stock, i) => (
                                            <TableRow key={i}>
                                                <TableCell>{stock.Product?.title || "Unknown"}</TableCell>
                                                <TableCell>{stock.quantity}</TableCell>
                                                <TableCell>GHS {parseFloat(stock.data?.cost || 0).toFixed(2)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                <p className="text-sm text-muted-foreground mt-2">
                                    To modify items, use the Returns feature.
                                </p>
                            </CardContent>
                        </Card>

                        {/* Notes */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Notes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    rows={3}
                                />
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                        {/* Discount & Tax */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Discount & Tax</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label>Discount Type</Label>
                                        <Select
                                            value={formData.discount_type}
                                            onValueChange={(val) => setFormData({ ...formData, discount_type: val })}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">None</SelectItem>
                                                <SelectItem value="percentage">Percentage</SelectItem>
                                                <SelectItem value="fixed">Fixed</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Value</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={formData.discount_value}
                                            onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                                            disabled={formData.discount_type === "none"}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label>Tax Rate (%)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={formData.tax_rate}
                                        onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Summary */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Summary</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex justify-between">
                                    <span>Subtotal</span>
                                    <span>GHS {subtotal.toFixed(2)}</span>
                                </div>
                                {discountAmount > 0 && (
                                    <div className="flex justify-between text-green-600">
                                        <span>Discount</span>
                                        <span>- GHS {discountAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {taxAmount > 0 && (
                                    <div className="flex justify-between">
                                        <span>Tax ({formData.tax_rate}%)</span>
                                        <span>+ GHS {taxAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between font-bold text-lg border-t pt-3">
                                    <span>Total</span>
                                    <span>GHS {grandTotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-muted-foreground">
                                    <span>Paid</span>
                                    <span>GHS {parseFloat(purchase?.amount_paid || 0).toFixed(2)}</span>
                                </div>
                            </CardContent>
                        </Card>

                        <Button type="submit" className="w-full" size="lg" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="mr-2 h-4 w-4" />
                                    Save Changes
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
}
